import React, { FC, useMemo } from 'react';

import { HStack } from '@chakra-ui/react';

import { SoftwareInstallationItem } from 'app/api/services/scanVaultSoftware';
import { StackedCell } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import {
  CheckmarkCircleOutlineIcon,
  LinkIcon,
  TagIcon,
} from 'components/Icons';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { toTitleCase } from 'utils/string';

interface Props {
  rows: SoftwareInstallationItem[];
  isLoading: boolean;
  isError: boolean;
}

export const SoftwareInventoryInstallationTable: FC<Props> = ({
  rows,
  isLoading,
  isError,
}) => {
  const { getCloudAgnosticName } = useResourceType();
  const { renderStackedCloudAccountIdWithIcon } = useCloudAccountId();
  const columns = useMemo(
    () => [
      {
        header: 'Entity',
        align: 'left',
        styles: { width: '18%', cell: { width: '18%' } },
        render: ({ row }) => (
          <StackedCell
            upper={row.resource_name || row.native_id || '—'}
            lower={getCloudAgnosticName(row.resource_type) || '—'}
            showUpperTooltip={false}
            showLowerTooltip={false}
            iconProps={{ resourceType: row.resource_type }}
          />
        ),
      },
      {
        header: 'EOL',
        styles: { width: '10%', cell: { width: '10%' } },
        render: () => '—',
      },
      {
        header: 'Account',
        align: 'left',
        styles: { width: '18%', cell: { width: '18%' } },
        render: ({ row }) =>
          renderStackedCloudAccountIdWithIcon(row.account_uuid),
      },

      {
        header: 'Deployment Type',
        align: 'left',
        styles: { width: '14%', cell: { width: '14%' } },
        render: ({ row }) => toTitleCase(row.deployment_type) || '—',
      },
      {
        header: 'Allow Status',
        styles: { width: '10%', cell: { width: '10%' } },
        render: () => '-',
      },
      {
        header: 'Actions',
        styles: { width: '16%', cell: { width: '16%' } },
        render: () => (
          <HStack spacing={0} justify="center">
            <ActionButton
              label="Set allow status"
              icon={<CheckmarkCircleOutlineIcon />}
              isDisabled
            />
            <ActionButton
              label="Configure context"
              icon={<TagIcon />}
              isDisabled
            />
            <ActionButton
              label="Copy resource link"
              icon={<LinkIcon />}
              isDisabled
            />
          </HStack>
        ),
      },
    ],
    [getCloudAgnosticName, renderStackedCloudAccountIdWithIcon],
  );

  return (
    <Table
      columns={columns}
      data={rows}
      isLoading={isLoading}
      isError={isError}
      noDataMessage="No software installations available"
    />
  );
};
